import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  User, Shield, Bell, Lock, Download, HelpCircle, 
  LogOut, ChevronRight, ArrowLeft, Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SettingsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/user/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const menuItems = [
    { id: 'account', icon: User, label: 'Account', desc: 'Name, email, mobile' },
    { id: 'privacy', icon: Shield, label: 'Privacy', desc: 'Who can see your content' },
    { id: 'security', icon: Lock, label: 'Security', desc: 'Password, login alerts' },
    { id: 'notifications', icon: Bell, label: 'Notifications', desc: 'Manage alerts' },
    { id: 'data', icon: Download, label: 'Your Data', desc: 'Download or delete' },
    { id: 'help', icon: HelpCircle, label: 'Help & Policies', desc: 'Support and legal' },
  ];

  const handleLogout = () => {
    onLogout();
    navigate('/auth');
  };

  if (activeSection) {
    return (
      <div className="w-full max-w-2xl mx-auto px-2 sm:px-4 py-4 pb-20 md:pb-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-3 p-4 border-b border-gray-200">
            <button
              onClick={() => setActiveSection(null)}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              data-testid="settings-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold">
              {menuItems.find(m => m.id === activeSection)?.label}
            </h2>
          </div>

          {/* Section Content */}
          <div className="p-4">
            {activeSection === 'account' && (
              <AccountSettings profile={profile} onUpdate={fetchProfile} />
            )}
            {activeSection === 'privacy' && (
              <PrivacySettings profile={profile} onUpdate={fetchProfile} />
            )}
            {activeSection === 'security' && (
              <SecuritySettings profile={profile} />
            )}
            {activeSection === 'notifications' && (
              <NotificationSettings />
            )}
            {activeSection === 'data' && (
              <DataSettings onLogout={handleLogout} />
            )}
            {activeSection === 'help' && (
              <HelpSettings />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto px-2 sm:px-4 py-4 pb-20 md:pb-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your account and preferences</p>
        </div>

        {/* User Info Card */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <img
              src={user?.avatar}
              alt={user?.name}
              className="w-14 h-14 rounded-full object-cover"
            />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">{user?.name}</h3>
              <p className="text-sm text-gray-500 truncate">{user?.email}</p>
            </div>
            <button
              onClick={() => navigate('/profile')}
              className="px-4 py-2 text-sm font-medium text-[#FF9933] hover:bg-orange-50 rounded-lg transition-colors"
              data-testid="view-profile-btn"
            >
              View Profile
            </button>
          </div>
        </div>

        {/* Menu Items */}
        <div className="divide-y divide-gray-100">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              data-testid={`settings-${item.id}-btn`}
              className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1 text-left min-w-0">
                <h3 className="font-medium text-gray-900">{item.label}</h3>
                <p className="text-sm text-gray-500 truncate">{item.desc}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
            </button>
          ))}
        </div>

        {/* Logout */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Account Settings Component
function AccountSettings({ profile, onUpdate }) {
  const [formData, setFormData] = useState({
    name: profile?.name || '',
    username: profile?.username || '',
    bio: profile?.bio || '',
    location: profile?.location || '',
    dob: profile?.dob || '',
    gender: profile?.gender || ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        username: profile.username || '',
        bio: profile.bio || '',
        location: profile.location || '',
        dob: profile.dob || '',
        gender: profile.gender || ''
      });
    }
  }, [profile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.put(`${API}/user/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:outline-none"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
        <input
          type="text"
          value={formData.username}
          onChange={(e) => setFormData({...formData, username: e.target.value})}
          placeholder="@username"
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:outline-none"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
        <textarea
          value={formData.bio}
          onChange={(e) => setFormData({...formData, bio: e.target.value})}
          rows={3}
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:outline-none"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
        <input
          type="text"
          value={formData.location}
          onChange={(e) => setFormData({...formData, location: e.target.value})}
          placeholder="City, State"
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:outline-none"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
          <input
            type="date"
            value={formData.dob}
            onChange={(e) => setFormData({...formData, dob: e.target.value})}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
          <select
            value={formData.gender}
            onChange={(e) => setFormData({...formData, gender: e.target.value})}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:outline-none"
          >
            <option value="">Select</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-3 bg-[#FF9933] hover:bg-[#E68A00] text-white font-medium rounded-lg disabled:opacity-50"
      >
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );
}

// Privacy Settings Component
function PrivacySettings({ profile, onUpdate }) {
  const [privacy, setPrivacy] = useState({
    profile_visibility: profile?.privacy?.profile_visibility || 'public',
    post_visibility: profile?.privacy?.post_visibility || 'public',
    allow_messages: profile?.privacy?.allow_messages || 'everyone',
    show_dob: profile?.privacy?.show_dob || false,
    show_contact: profile?.privacy?.show_contact || false,
    show_last_seen: profile?.privacy?.show_last_seen !== false,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.put(`${API}/user/privacy`, privacy, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Privacy settings updated');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update privacy settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Profile Visibility</label>
        <select
          value={privacy.profile_visibility}
          onChange={(e) => setPrivacy({...privacy, profile_visibility: e.target.value})}
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:outline-none"
        >
          <option value="public">Public (Everyone)</option>
          <option value="users">Logged-in Users Only</option>
          <option value="only_me">Only Me</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Who Can See Posts</label>
        <select
          value={privacy.post_visibility}
          onChange={(e) => setPrivacy({...privacy, post_visibility: e.target.value})}
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:outline-none"
        >
          <option value="public">Public</option>
          <option value="users">Logged-in Users Only</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Who Can Message Me</label>
        <select
          value={privacy.allow_messages}
          onChange={(e) => setPrivacy({...privacy, allow_messages: e.target.value})}
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:outline-none"
        >
          <option value="everyone">Everyone</option>
          <option value="followers">Followers Only</option>
          <option value="none">No One</option>
        </select>
      </div>

      <div className="space-y-3">
        <label className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Show Date of Birth</span>
          <input
            type="checkbox"
            checked={privacy.show_dob}
            onChange={(e) => setPrivacy({...privacy, show_dob: e.target.checked})}
            className="w-5 h-5 text-[#FF9933] rounded focus:ring-orange-400"
          />
        </label>

        <label className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Show Contact Info</span>
          <input
            type="checkbox"
            checked={privacy.show_contact}
            onChange={(e) => setPrivacy({...privacy, show_contact: e.target.checked})}
            className="w-5 h-5 text-[#FF9933] rounded focus:ring-orange-400"
          />
        </label>

        <label className="flex items-center justify-between" data-testid="privacy-show-last-seen">
          <span className="text-sm font-medium text-gray-700">Show my last seen / online status</span>
          <input
            type="checkbox"
            checked={privacy.show_last_seen}
            onChange={(e) => setPrivacy({...privacy, show_last_seen: e.target.checked})}
            className="w-5 h-5 text-[#FF9933] rounded focus:ring-orange-400"
          />
        </label>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-3 bg-[#FF9933] hover:bg-[#E68A00] text-white font-medium rounded-lg disabled:opacity-50"
      >
        {saving ? 'Saving...' : 'Save Privacy Settings'}
      </button>
    </div>
  );
}

// Security Settings Component
function SecuritySettings({ profile }) {
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [changing, setChanging] = useState(false);

  const handleChangePassword = async () => {
    if (passwords.new !== passwords.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.new.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setChanging(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/user/change-password`, {
        current_password: passwords.current,
        new_password: passwords.new
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password changed successfully');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChanging(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-medium text-gray-900 mb-4">Change Password</h3>
        <div className="space-y-3">
          <input
            type="password"
            placeholder="Current Password"
            value={passwords.current}
            onChange={(e) => setPasswords({...passwords, current: e.target.value})}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:outline-none"
          />
          <input
            type="password"
            placeholder="New Password"
            value={passwords.new}
            onChange={(e) => setPasswords({...passwords, new: e.target.value})}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:outline-none"
          />
          <input
            type="password"
            placeholder="Confirm New Password"
            value={passwords.confirm}
            onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:outline-none"
          />
          <button
            onClick={handleChangePassword}
            disabled={changing}
            className="w-full py-3 bg-[#FF9933] hover:bg-[#E68A00] text-white font-medium rounded-lg disabled:opacity-50"
          >
            {changing ? 'Changing...' : 'Change Password'}
          </button>
        </div>
      </div>

      <div className="pt-4 border-t border-gray-200">
        <h3 className="font-medium text-gray-900 mb-4">Security Options</h3>
        <div className="space-y-3">
          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700">Login Alerts</span>
            <input
              type="checkbox"
              defaultChecked={true}
              className="w-5 h-5 text-[#FF9933] rounded focus:ring-orange-400"
            />
          </label>
          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700">Two-Factor Authentication</span>
            <span className="text-xs text-gray-400">Coming Soon</span>
          </label>
        </div>
      </div>
    </div>
  );
}

// Notification Settings Component
function NotificationSettings() {
  const [settings, setSettings] = useState({
    namaste: true,
    saved: false,
    messages: true,
    live_aarti: true
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">Choose what notifications you receive</p>
      
      {[
        { key: 'namaste', label: 'Namaste (Like) Notifications' },
        { key: 'saved', label: 'When someone saves your post' },
        { key: 'messages', label: 'New message alerts' },
        { key: 'live_aarti', label: 'Live Aarti reminders' },
      ].map((item) => (
        <label key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <span className="text-sm text-gray-700">{item.label}</span>
          <input
            type="checkbox"
            checked={settings[item.key]}
            onChange={(e) => setSettings({...settings, [item.key]: e.target.checked})}
            className="w-5 h-5 text-[#FF9933] rounded focus:ring-orange-400"
          />
        </label>
      ))}
      
      <button className="w-full py-3 bg-[#FF9933] hover:bg-[#E68A00] text-white font-medium rounded-lg mt-4">
        Save Preferences
      </button>
    </div>
  );
}

// Data Settings Component
function DataSettings({ onLogout }) {
  const [downloading, setDownloading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/user/download-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'dharmbook_data.json';
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Data downloaded successfully');
    } catch (error) {
      toast.error('Failed to download data');
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.delete(`${API}/user/delete`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Account scheduled for deletion');
      onLogout();
    } catch (error) {
      toast.error('Failed to delete account');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-medium text-gray-900 mb-2">Download Your Data</h3>
        <p className="text-sm text-gray-500 mb-4">
          Get a copy of your DharmBook data including posts, messages, and profile info.
        </p>
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="w-full py-3 border border-[#FF9933] text-[#FF9933] hover:bg-orange-50 font-medium rounded-lg"
        >
          {downloading ? 'Preparing...' : 'Download Data (JSON)'}
        </button>
      </div>

      <div className="pt-4 border-t border-gray-200">
        <h3 className="font-medium text-red-600 mb-2">Danger Zone</h3>
        <p className="text-sm text-gray-500 mb-4">
          Once you delete your account, there is no going back. Please be certain.
        </p>
        
        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full py-3 border border-red-500 text-red-500 hover:bg-red-50 font-medium rounded-lg"
          >
            Delete Account
          </button>
        ) : (
          <div className="space-y-3 p-4 bg-red-50 rounded-lg">
            <p className="text-sm text-red-600 font-medium">
              Are you sure? This action cannot be undone.
            </p>
            <p className="text-xs text-red-500">
              Your data will be permanently removed after 30 days.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2 border border-gray-300 text-gray-600 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 py-2 bg-red-500 text-white rounded-lg"
              >
                Delete Forever
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Help Settings Component
function HelpSettings() {
  return (
    <div className="space-y-4">
      {[
        { label: 'Help Center', desc: 'Get help with using DharmBook' },
        { label: 'Privacy Policy', desc: 'How we handle your data' },
        { label: 'Terms of Service', desc: 'Rules and guidelines' },
        { label: 'Community Guidelines', desc: 'Sanatan values we follow' },
        { label: 'Contact Us', desc: 'Reach out for support' },
      ].map((item) => (
        <button
          key={item.label}
          className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
        >
          <div className="text-left">
            <h4 className="font-medium text-gray-900">{item.label}</h4>
            <p className="text-xs text-gray-500">{item.desc}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </button>
      ))}
      
      <div className="pt-4 text-center">
        <p className="text-sm text-gray-400">DharmBook v1.0</p>
        <p className="text-xs text-gray-400 mt-1">Made with 🙏 in Bharat</p>
      </div>
    </div>
  );
}
